import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AlertController, IonInput, ModalController, ToastController } from '@ionic/angular';
import { from } from 'linq-to-typescript';
import { SpartaxAuftragskunde, SpartaxAuftragskundeArt, SpartaxErklaerungsjahrUtil } from 'src/app/data/dto/spartax/spartax-auftragskunde.dto';
import { SpartaxFolgeAuftragDto } from 'src/app/data/dto/spartax/spartax-auftrag.dto';
import { SpartaxKind, SpartaxKunde, SpartaxKundeUtil } from 'src/app/data/dto/spartax/spartax-kunde.dto';
import { SpartaxService } from 'src/app/data/service/spartax.service';
import { SpartaxFormStepBaseComponent } from '../spartax-form-step-base';
import { SpartaxUtils } from 'src/app/utils/spartax-utils';
import { SpartaxFollowUpDialogComponent } from '../spartax-follow-up-dialog/spartax-follow-up-dialog.component';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';

@Component({
  selector: 'app-spartax-identity',
  templateUrl: './spartax-identity.component.html',
  styleUrls: ['./spartax-identity.component.scss'],
})
export class SpartaxIdentityComponent extends SpartaxFormStepBaseComponent implements OnInit {
  private _kunde!: SpartaxKunde;
  @ViewChild('svNummerInput') svNummerInput?: IonInput;

  @Input()
  set kunde(value: SpartaxKunde) {
    this._kunde = value;
    if (value?.Nachname) {
      this.existingKundeSearched = true;
    }
  }

  get kunde(): SpartaxKunde {
    return this._kunde;
  }

  @Input()
  isHauptkunde: boolean;

  @Input()
  debug: boolean;

  @Input()
  teilDesAuftrages: boolean;

  @Input()
  spartaxPortalKunden: Map<string, SpartaxKunde>;

  @Input()
  auftragskunden: SpartaxAuftragskunde[];

  @Input()
  kinder: SpartaxKind[];

  @Output()
  kundeChange = new EventEmitter<SpartaxKunde>();

  @Output()
  spartaxPortalKundenChange = new EventEmitter<Map<string, SpartaxKunde>>();

  @Output()
  auftragskundenChange = new EventEmitter<SpartaxAuftragskunde[]>();

  @Output()
  kinderChange = new EventEmitter<SpartaxKind[]>();

  public errorMsg = "";
  public possibleKundenMatches: {key: string, value: string}[] = [];
  public queryingKundenSv: boolean = false;
  public loadingKunde: boolean = false;
  public existingKundeSearched: boolean = false;
  public skipSvSearch: boolean = false;
  public readonly allowSkipSvSearch: boolean = environment.allowSkipSvSearch;
  public readonly allowOverrideErklaerungsjahre: boolean = environment.allowOverrideErklaerungsjahre;
  public overrideErklaerungsjahre: boolean = false;
  public overrideFirstYear: number = moment().year() - 6;
  public overrideLastYear: number = moment().year() - 1;

  private kundeBackup: SpartaxKunde;

  constructor(
    private spartaxService: SpartaxService,
    private alertController: AlertController,
    private modalController: ModalController,
    private toastController: ToastController
  ) {
    super();
  }

  ngOnInit() {
    if (this.model?.overrideErklaerungsjahreFirstYear != null && this.model?.overrideErklaerungsjahreLastYear != null) {
      this.overrideErklaerungsjahre = true;
      this.overrideFirstYear = this.model.overrideErklaerungsjahreFirstYear;
      this.overrideLastYear = this.model.overrideErklaerungsjahreLastYear;
    }
  }

  override formIsValid() {
    this.errorMsg = '';
    if (this.edit) {
      if (!super.formIsValid()) {
        return false;
      }
      const formValues = this.ngForm.form.value || {};
      const valueBySuffix = (suffix: string) => {
        const key = Object.keys(formValues).find(x => x.endsWith(suffix));
        return key ? formValues[key] : null;
      };

      const svNummerValue = valueBySuffix('SVNummerWriteRequired');
      const svNummerDigits = String(svNummerValue ?? '').replace(/\D/g, '');
      if (svNummerDigits && !SpartaxUtils.validateSvNummer(Number(svNummerDigits))) {
        this.errorMsg = 'Die eingegebene SV-Nummer ist ungültig.';
        return false;
      }
      if (!this.existingKundeSearched && !this.skipSvSearch) {
        this.errorMsg = 'Bitte suchen Sie nach einem bestehenden Kunden, bevor Sie fortfahren.';
        return false;
      }

      const requiredIdentityFields = [
        'NachnameWriteRequired',
        'VornameWriteRequired'
      ];
      const hasAllRequiredIdentityValues = requiredIdentityFields.every(suffix => {
        const value = valueBySuffix(suffix);
        return value !== null && value !== undefined && (value + '').trim().length > 0;
      });

      if (!hasAllRequiredIdentityValues) {
        this.errorMsg = 'Bitte füllen Sie alle Pflichtfelder aus.';
        return false;
      }
    }
    return true;
  }

  onVornameChange(value: string) {
    this.kunde.Vorname = value;

    //Because we match the current partners name with the new name, we need to update the partner name as well
    const auftragsKunde = this.auftragskunden.find(x => x.KundeFormModelId === this.kunde.FormModelId);
    if (auftragsKunde && auftragsKunde.AuftragsKundeArt == SpartaxAuftragskundeArt.aktuellerPartner) {
      const hauptkunde = this.auftragskunden.find(x => x.AuftragsKundeArt == SpartaxAuftragskundeArt.hauptkunde);
      hauptkunde.Kunde.Familienstaende[0].VornamePartner = this.kunde.Vorname;

      if (this.kinder) {
        for (const kind of this.kinder) {
          if (this.kunde.Geschlecht == 'Weiblich' && !kind.MutterIstExPartner) {
            kind.Mutter = this.kunde.Vorname;
          }

          if (this.kunde.Geschlecht == 'Männlich' && !kind.VaterIstExPartner) {
            kind.Vater = this.kunde.Vorname;
          }
        }
      }
    }
  }

  get canQueryKundenBySv() {
    return this.kunde.SVNummer && SpartaxUtils.validateSvNummer(this.kunde.SVNummer) && !this.queryingKundenSv;
  }

  onSVNummerChange(value: string | number) {
    const digitsOnly = String(value ?? '').replace(/\D/g, '');
    this.kunde.SVNummer = digitsOnly ? Number(digitsOnly) : undefined;
  }

  onSkipSvSearchChange(checked: boolean) {
    if (checked) {
      this.kunde.SVNummer = this.generateRandomSvNummer();
    }
  }

  onOverrideErklaerungsjahreChange(checked: boolean) {
    this.applyErklaerungsjahreOverride();
  }

  applyErklaerungsjahreOverride() {
    if (!this.model) {
      return;
    }

    let firstYear: number;
    let lastYear: number;

    if (this.overrideErklaerungsjahre && this.overrideFirstYear <= this.overrideLastYear) {
      this.model.overrideErklaerungsjahreFirstYear = this.overrideFirstYear;
      this.model.overrideErklaerungsjahreLastYear = this.overrideLastYear;
      firstYear = this.overrideFirstYear;
      lastYear = this.overrideLastYear;
    } else {
      this.model.overrideErklaerungsjahreFirstYear = undefined;
      this.model.overrideErklaerungsjahreLastYear = undefined;
      firstYear = moment().year() - 6;
      lastYear = moment().year() - 1;
    }

    if (this.auftragskunden) {
      for (const auftragskunde of this.auftragskunden) {
        auftragskunde.Erklaerungsjahre = SpartaxErklaerungsjahrUtil.initErklaerungsjahre(firstYear, lastYear);
      }
    }

    this.modelChange.emit(this.model);
    this.auftragskundenChange.emit(this.auftragskunden);
  }

  private generateRandomSvNummer(): number {
    const rnd = () => Math.floor(Math.random() * 10);
    while (true) {
      const d = Array.from({ length: 9 }, rnd);
      // d[0..2] → positions 0-2, d[3..8] → positions 4-9
      const checkDigit = (
        d[0] * 3 + d[1] * 7 + d[2] * 9 +
        d[3] * 5 + d[4] * 8 + d[5] * 4 +
        d[6] * 2 + d[7] * 1 + d[8] * 6
      ) % 11;
      if (checkDigit <= 9) {
        const svStr = `${d[0]}${d[1]}${d[2]}${checkDigit}${d[3]}${d[4]}${d[5]}${d[6]}${d[7]}${d[8]}`;
        return Number(svStr);
      }
    }
  }

  async queryKundenBySv() {
    this.errorMsg = '';
    if (this.canQueryKundenBySv) {
      this.queryingKundenSv = true;
      try {
        const kundeFromSvNumber = await this.spartaxService.getCustomerBySvNumber(this.kunde.SVNummer);
        if (kundeFromSvNumber) {
          const folgeAuftragData = await this.loadFollowUpOrderData(kundeFromSvNumber.ListItemId);
          this.queryingKundenSv = false;
          await this.handleKundeLookupResult(kundeFromSvNumber, folgeAuftragData);
        } else {
          const toast = await this.toastController.create({
            message: 'Über diese Person gibt es noch keine gespeicherten Daten',
            duration: 3000,
            icon: 'information-circle-outline'
          });
          await toast.present();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.queryingKundenSv = false;
        this.existingKundeSearched = true;
      }
    } else {
      const alert = await this.alertController.create({
        subHeader: 'Ungültige SV-Nummer',
        message: 'Die eingegebene SV-Nummer ist ungültig. Bitte überprüfen Sie die Eingabe.',
        buttons: ['OK']
      });

      const onEnterConfirm = async (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          await alert.dismiss(undefined, 'confirm');
        }
      };

      await this.presentAlertWithEnterConfirm(alert, onEnterConfirm);

      await this.focusSvInputAfterAlert();
    }
  }

  private async presentAlertWithEnterConfirm(alert: HTMLIonAlertElement, onEnterConfirm: (event: KeyboardEvent) => Promise<void>) {
    document.addEventListener('keydown', onEnterConfirm);
    await alert.present();
    await alert.onDidDismiss();
    document.removeEventListener('keydown', onEnterConfirm);
  }

  private async focusSvInputAfterAlert() {
    for (let attempt = 0; attempt < 6; attempt++) {
      if (this.svNummerInput) {
        await this.svNummerInput.setFocus();
        const nativeInput = await this.svNummerInput.getInputElement();
        nativeInput.focus();
        nativeInput.select();
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 150));
    }
  }

  private async loadFollowUpOrderData(listItemId: number): Promise<SpartaxFolgeAuftragDto | null> {
    try {
      return await this.spartaxService.getFollowUpOrderData(listItemId);
    } catch (error) {
      console.warn('Folgeauftrag-Daten konnten nicht geladen werden.', error);
      return null;
    }
  }

  private async handleKundeLookupResult(kundeFromSvNumber: SpartaxKunde, folgeAuftragData: SpartaxFolgeAuftragDto | null) {
    if (!folgeAuftragData) {
      await this.onExistingCustomerSelected(kundeFromSvNumber.ListItemId.toString());
      return;
    }

    const alert = await this.alertController.create({
      subHeader: 'Folgeauftrag möglich',
      message: 'Diese Person war bereits einmal Teil eines Auftrags. Möchten Sie einen Folgeauftrag starten?',
      buttons: [
        {
          text: 'Nein',
          role: 'existing'
        },
        {
          text: 'Ja',
          role: 'followup'
        }
      ]
    });

    await alert.present();
    const result = await alert.onDidDismiss();

    if (result.role === 'existing') {
      await this.onExistingCustomerSelected(kundeFromSvNumber.ListItemId.toString());
      return;
    }

    if (result.role === 'followup') {
      await this.openFollowUpDialog(folgeAuftragData);
    }
  }

  private async openFollowUpDialog(folgeAuftragData: SpartaxFolgeAuftragDto) {
    const modal = await this.modalController.create({
      component: SpartaxFollowUpDialogComponent,
      cssClass: 'beeex-modal beeex-modal-spartax-follow-up',
      componentProps: {
        followUpData: folgeAuftragData
      }
    });

    await modal.present();
    const result = await modal.onDidDismiss();
    if (result.role === 'start-follow-up' && result.data?.followUpData) {
      this.startFollowUp(result.data.followUpData);
    }
  }

  private startFollowUp(folgeAuftragData: SpartaxFolgeAuftragDto) {
    const referenceFormId = folgeAuftragData.ReferenceBeeexpertFormId;
    const referenceFormFilloutId = folgeAuftragData.ReferenceBeeexpertFormFilloutId;

    const queryParams = new URLSearchParams({
      edit: 'false',
      copy: 'true',
      followup: JSON.stringify(folgeAuftragData)
    });

    window.location.href = `/forms/fillout/${referenceFormId}/${referenceFormFilloutId}?${queryParams.toString()}`;
  }

  async onExistingCustomerSelected(id: string) {
    /*
      NOTE -> Currently, this method is duplicated in the spartax-identity and spartax-place-order components,
      because a change to the functionality is already planned and will be implemented in the future.
    */
    if (from(this.auftragskunden).count(x => x.Kunde.ListItemId === +id) > 1) {
      const alert = await this.alertController.create({
        subHeader: 'Der ausgewählte Kunde ist bereits einem anderen Auftragskunden zugeordnet.',
        buttons: ['OK']
      });

      const onEnterConfirm = async (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          await alert.dismiss(undefined, 'confirm');
        }
      };

      await this.presentAlertWithEnterConfirm(alert, onEnterConfirm);
    } else {
      this.loadingKunde = true;
      this.kundeBackup = { ...this.kunde };
      const kundeFromSharePoint = await this.spartaxService.getCustomerById(+id);
      SpartaxKundeUtil.updateKundePropsFromAnother(kundeFromSharePoint, this.kunde);
      this.kunde.procedureDiscussed = true;
      this.kunde.Partner = this.kundeBackup.Partner;
      this.kunde.FormModelIdPartner = this.kundeBackup.FormModelIdPartner;
      this.kunde.ListItemIdPartner = this.kundeBackup.ListItemIdPartner;
      this.kunde.Familienstaende = this.kundeBackup.Familienstaende;
      this.kunde.GemeinsamerHaushalt = this.kundeBackup.GemeinsamerHaushalt;
      this.kundeChange.emit(this.kunde);

      //Swap all references to the old formModelId in auftragskunden
      for (const auftragskunde of this.auftragskunden) {
        if (auftragskunde.KundeFormModelId === this.kundeBackup.FormModelId) {
          auftragskunde.KundeFormModelId = kundeFromSharePoint.FormModelId;
          //The auftragskunde is now linked to an existing customer, so it is no longer a Neukunde
          auftragskunde.Neukunde = false;
        }

        if (auftragskunde.Kunde.FormModelIdPartner === this.kundeBackup.FormModelId) {
          auftragskunde.Kunde.FormModelIdPartner = kundeFromSharePoint.FormModelId;
        }

        if (auftragskunde.Kunde.ListItemIdPartner === this.kundeBackup.ListItemId) {
          auftragskunde.Kunde.ListItemIdPartner = kundeFromSharePoint.ListItemId;
        }

        if (auftragskunde.Kunde.FormModelIdMutter === this.kundeBackup.FormModelId) {
          auftragskunde.Kunde.FormModelIdMutter = kundeFromSharePoint.FormModelId;
        }

        if (auftragskunde.Kunde.FormModelIdVater === this.kundeBackup.FormModelId) {
          auftragskunde.Kunde.FormModelIdVater = kundeFromSharePoint.FormModelId;
        }

        if (auftragskunde.Kunde.FormModelIdsKinder) {
          for (let i = 0; i < auftragskunde.Kunde.FormModelIdsKinder.length; i++) {
            if (auftragskunde.Kunde.FormModelIdsKinder[i] === this.kundeBackup.FormModelId) {
              auftragskunde.Kunde.FormModelIdsKinder[i] = kundeFromSharePoint.FormModelId;
            }
          }
        }

        if (auftragskunde.Kunde.ListItemIdsKinder) {
          for (let i = 0; i < auftragskunde.Kunde.ListItemIdsKinder.length; i++) {
            if (auftragskunde.Kunde.ListItemIdsKinder[i] === this.kundeBackup.ListItemId) {
              auftragskunde.Kunde.ListItemIdsKinder[i] = kundeFromSharePoint.ListItemId;
            }
          }
        }
      }

      this.auftragskundenChange.emit(this.auftragskunden);

      for (const kind of this.kinder) {
        if (kind.KundeFormModelId === this.kundeBackup.FormModelId) {
          kind.KundeFormModelId = kundeFromSharePoint.FormModelId;
        }
      }
      this.kinderChange.emit(this.kinder);

      this.spartaxPortalKunden.set(this.kunde.FormModelId, this.kunde);
      this.spartaxPortalKundenChange.emit(this.spartaxPortalKunden);

      this.loadingKunde = false;
      this.existingKundeSearched = true;
    }
  }
}
